/**
 * Compresses an image file to reduce its size while keeping original dimensions
 * @param file - The original image file
 * @param targetSizeMB - Target file size in MB (default: 1)
 * @returns Promise<File> - The compressed image file
 */
export const compressImage = async (
  file: File,
  targetSizeMB: number = 1
): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Keep original dimensions
      const { width, height } = img;
      canvas.width = width;
      canvas.height = height;

      // Draw image
      ctx?.drawImage(img, 0, 0, width, height);
      
      // Start with high quality and reduce until target size
      const targetSizeBytes = targetSizeMB * 1024 * 1024;
      let quality = 0.9;
      
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              if (blob.size <= targetSizeBytes || quality <= 0.1) {
                // Target size reached or minimum quality reached
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                // Reduce quality and try again
                quality -= 0.1;
                tryCompress();
              }
            } else {
              resolve(file); // Fallback to original if compression fails
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      tryCompress();
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Compresses multiple image files
 * @param files - Array of image files
 * @returns Promise<File[]> - Array of compressed image files
 */
export const compressImages = async (files: File[]): Promise<File[]> => {
  const compressionPromises = files.map(file => compressImage(file));
  return Promise.all(compressionPromises);
}; 